import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { connect, ConnectedProps } from "react-redux";
import { actions, slotDetailSelector } from "reducers/slotDetailModal";
import { actions as sessionActions, sessionSelector } from "reducers/session";
import {
  PickersSlotRequestType,
  SlotDetailUpdateBody,
  SlotDetailUpdateParams,
} from "sagas/types/slotDetailModal";
import { AppDispatch, RootState } from "store";
import { formatDDMMYYYY, formatHHMM } from "utils/date";
import { SlotsDetailModal } from "./SlotsDetailModal";
import { SlotDetailValuesType, SlotsDetailModalContainerProps } from "./types";
import { NotificationStateType } from "reducers/types/notification";
import { actions as notificationActions } from "reducers/notification";
import { DropdownState } from "reducers/types/slotDetailModal";
import { useTranslation } from "react-i18next";
import { dirtyAlert } from "utils/alerts";
import { FormRefType } from "utils/types";

const SlotsDetailModalContainer: React.FC<
  SlotsDetailModalContainerProps & ConnectorProps
> = (props): JSX.Element => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dropdownContainerRef = useRef<HTMLDivElement>(null);
  const [resolutionHeightModal, setresolutionHeightModal] = useState(550);
  const { slot, isOpen } = props;
  const testId = "slot-detail";

  useEffect(() => {
    if (isOpen) {
      if (window.screen.availHeight < 760) {
        setresolutionHeightModal(550);
      }
      if (window.screen.availHeight > 900) {
        setresolutionHeightModal(675);
      }
    } else {
      props.resetNewPicker();
      props.resetPickers();
      props.setDropdownState("closed");
    }

    return () => {
      props.resetNewPicker();
      props.resetPickers();
      props.setDropdownState("closed");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.resetNewPicker, props.resetPickers, isOpen]);

  const deleteSlot = (id: number) => {
    props.showNotification({
      title: t("slotDetail:deleteTitle"),
      body: t("slotDetail:deleteMessage"),
      level: "warning",
      onCancelLabel: "global:cancel",
      onConfirmLabel: "global:deleteConfirmation",
      onConfirm: () => props.deleteSlot(id),
    });
  };

  const updateSlot = (values: SlotDetailValuesType) => {
    const body: SlotDetailUpdateBody = {
      capacity: values.capacity,
      startAt: `${values.slotTime.initial}:00`,
      finishAt: `${values.slotTime.end}:00`,
    };
    const params: SlotDetailUpdateParams = {
      body,
      regionId: slot.region.id,
      id: slot.id,
      refresh: values.refresh,
    };
    props.updateSlot(params);
  };

  const handleRefresh = useCallback(() => {
    if (!slot?.id) return;
    props.getSlot(slot?.id);
    if (props.dropdownState !== "closed") {
      props.getPickers({
        slotId: slot.id,
        connected: props.dropdownState === "connected",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot?.id, props.getSlot, props.dropdownState]);

  const onSumbit = (values: SlotDetailValuesType) => {
    if (props.newPickerDirty) {
      if (props.pickerErrorCode) {
        showErrorsWarning(() => updateSlot(values));
      } else {
        showPickerInscriptionWarning(() => updateSlot(values));
      }
    } else if (
      values.slotTime.end > formatHHMM(slot.finishAt) ||
      values.slotTime.initial < formatHHMM(slot.startAt)
    ) {
      showOverlapedPickersWarning(() => updateSlot(values));
    } else {
      updateSlot(values);
    }
  };

  const toggleDropdown = useCallback(
    (
      tab: DropdownState,
      e?: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      e?.stopPropagation();
      if (!props.slot?.id || props.newPickerDirty || props.loading) return;
      if (props.dropdownState === tab) {
        props.setDropdownState("closed");
      } else {
        props.setDropdownState(tab);
        props.getPickers({
          connected: tab === "connected",
          slotId: props.slot?.id || 0,
        });
      }
    },
    [props]
  );

  const showPickerInscriptionWarning = (onClose?: () => void) => {
    return props.showNotification({
      id: "notification-dirty",
      level: "warning",
      title: t("slotDetail:newPickerTitle"),
      body: t("slotDetail:newPickerMessage"),
      onConfirmLabel: "global:goToSave",
      onCancelLabel: "global:notSave",
      onCancel: onClose,
      onConfirm: scroll,
    });
  };

  const showOverlapedPickersWarning = (onConfirm?: () => void) => {
    return props.showNotification({
      id: "notification-dirty",
      level: "warning",
      title: t("slotDetail:overlapedPickersTitle"),
      body: t("slotDetail:overlapedPickersMessage"),
      onConfirmLabel: "global:confirm",
      onCancelLabel: "global:back",
      onConfirm: onConfirm,
    });
  };

  const showErrorsWarning = (onClose: () => void) => {
    const handleCancel = () => {
      onClose();
      props.setDirty(false);
    };
    return props.showNotification({
      id: "notification-file-error",
      level: "warning",
      title: t("global:saveError"),
      body: t("alerts:withoutSavingMessage"),
      onConfirmLabel: "global:checkErrors",
      onCancelLabel: "global:continue",
      onCancel: handleCancel,
      onConfirm: scroll,
    });
  };

  const formRef: FormRefType<SlotDetailValuesType> = useRef(null);

  const handleClose = () => {
    const close = () => props.onClose();
    if (props.dirty || props.newPickerDirty) {
      if (props.pickerErrorCode || props.invalid) {
        showErrorsWarning(close);
      } else if (props.newPicker) {
        showPickerInscriptionWarning(close);
      } else {
        props.showNotification(dirtyAlert({ onCancel: close, scroll: false }));
      }
    } else {
      close();
    }
  };

  useEffect(() => {
    formRef.current?.restart();
  }, [isOpen]);

  useEffect(() => {
    if (
      window.screen.availHeight > 900 &&
      props.slot?.id &&
      isOpen &&
      props.loadedDetail
    ) {
      props.setDropdownState("enrolled");
      props.getPickers({
        connected: false,
        slotId: props.slot?.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.slot?.id, props.getPickers, isOpen]);

  const initialValues = useMemo(
    () => ({
      date: slot.startAt && formatDDMMYYYY(slot.startAt),
      slotTime: {
        initial: slot.startAt && formatHHMM(slot.startAt),
        end: slot.finishAt && formatHHMM(slot.finishAt),
      },
      region: slot.region?.name,
      workzone: slot.workZone?.name,
      enroled: slot.taken,
      connected: slot.connected,
      capacity: slot.capacity,
    }),
    [slot]
  );

  const scroll = useCallback(() => {
    if (dropdownContainerRef.current && scrollContainerRef.current) {
      const scrollPosition = scrollContainerRef.current.getBoundingClientRect();
      const dropdownpostion =
        dropdownContainerRef.current.getBoundingClientRect();
      const delta = dropdownpostion.top - scrollPosition.top;
      scrollContainerRef.current.scroll({
        top: scrollContainerRef.current.scrollTop + delta,
        left: 0,
        behavior: "smooth",
      });
    }
  }, []);

  return (
    <SlotsDetailModal
      {...props}
      scrollContainerRef={scrollContainerRef}
      dropdownContainerRef={dropdownContainerRef}
      resolutionModal={resolutionHeightModal}
      toggleDropdown={toggleDropdown}
      initialValues={initialValues}
      deleteSlotConfirm={deleteSlot}
      onSubmit={onSumbit}
      onClose={handleClose}
      onRefresh={handleRefresh}
      scroll={scroll}
      formRef={formRef}
      testId={testId}
    />
  );
};
const mapStateToProps = (state: RootState) => ({
  dirty: sessionSelector(state).dirtyForm,
  invalid: sessionSelector(state).invalidForm,
  loading: slotDetailSelector(state).loading,
  slot: slotDetailSelector(state).slot,
  dropdownState: slotDetailSelector(state).dropdownState,
  newPicker: slotDetailSelector(state).newPicker,
  newPickerDirty: slotDetailSelector(state).newPickerDirty,
  pickerErrorCode: slotDetailSelector(state).pickerErrorCode,
  loadedDetail: slotDetailSelector(state).loadedDetail,
});
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getSlot: (slotId: number) => {
    dispatch(actions.getSlotDetailRequest(slotId));
  },
  getPickers: (params: PickersSlotRequestType) => {
    dispatch(actions.getPickersFetch(params));
  },
  deleteSlot: (id: number) => {
    dispatch(actions.deleteSlotRequest(id));
  },
  showNotification: (content: NotificationStateType) => {
    dispatch(notificationActions.showNotification(content));
  },
  updateSlot: (params: SlotDetailUpdateParams) => {
    dispatch(actions.updateSlotDetailRequest(params));
  },
  setDropdownState: (state: DropdownState) => {
    dispatch(actions.setDropdownState(state));
  },
  resetNewPicker: () => {
    dispatch(actions.resetNewPicker());
  },
  resetPickers: () => {
    dispatch(actions.resetPickers());
  },
  setDirty: (dirty: boolean) => {
    dispatch(sessionActions.setDirty(dirty));
  },
  setInvalid: (invalid: boolean) => {
    dispatch(sessionActions.setInvalid(invalid));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(SlotsDetailModalContainer);
